import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import { getIconComponent } from '../utils/iconMapper';
import PageLoader from '../components/PageLoader';
import {
    Check,
    Star,
    ArrowRight,
    Shield,
    Sparkles,
    Users,
    Award,
    Crown,
    Percent,
    Calendar,
    Zap,
    HelpCircle,
    TrendingUp,
    Building2,
    Church,
    Gem,
} from 'lucide-react';

function Pricing() {
    const { data: plansData, loading, error } = useFetch('/pricing');

    const [billingCycle, setBillingCycle] = useState('monthly');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [hoveredPlan, setHoveredPlan] = useState(null);

    // Transform plans: add icon component
    const plans = useMemo(() => {
        if (!plansData) return [];
        return plansData.map((plan) => ({
            ...plan,
            icon: getIconComponent(plan.icon) || Sparkles,
            // Ensure features is an array
            features: plan.features || [],
        }));
    }, [plansData]);

    // Set default selected plan to featured or first
    useEffect(() => {
        if (plans.length > 0 && !selectedPlan) {
            const featured = plans.find((p) => p.is_featured);
            setSelectedPlan(featured ? featured.id : plans[0].id);
        }
    }, [plans, selectedPlan]);

    // Billing helpers
    const getMultiplier = (cycle) => {
        switch (cycle) {
            case 'monthly':
                return 1;
            case 'quarterly':
                return 0.95;
            case 'biannual':
                return 0.90;
            case 'annual':
                return 0.80;
            default:
                return 1;
        }
    };

    const getPeriodText = (cycle) => {
        switch (cycle) {
            case 'monthly':
                return '/mo';
            case 'quarterly':
                return '/quarter';
            case 'biannual':
                return '/6mo';
            case 'annual':
                return '/yr';
            default:
                return '/mo';
        }
    };

    const getAdjustedPrice = (plan, cycle) => {
        if (plan.is_custom || plan.price === 0) return plan.price;
        const multiplier = getMultiplier(cycle);
        const annualPrice = parseFloat(plan.price) * 12 * multiplier;
        return cycle === 'monthly'
            ? parseFloat(plan.price)
            : Math.round(annualPrice / (cycle === 'monthly' ? 12 : cycle === 'quarterly' ? 4 : cycle === 'biannual' ? 2 : 1));
    };

    const getSavings = (plan, cycle) => {
        if (plan.is_custom || plan.price === 0 || cycle === 'monthly') return null;
        const regular = parseFloat(plan.price);
        const adjusted = getAdjustedPrice(plan, cycle);
        const savings = regular - adjusted;
        return savings > 0 ? `Save GH₵ ${savings}/mo` : null;
    };

    const billingOptions = [
        { id: 'monthly', label: 'Monthly', discount: null, icon: '📅' },
        { id: 'quarterly', label: 'Quarterly', discount: '5%', icon: '📆' },
        { id: 'biannual', label: 'Biannual', discount: '10%', icon: '📊' },
        { id: 'annual', label: 'Annual', discount: '20%', icon: '📈' },
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Loading state
    if (loading) {
        return <PageLoader message="Loading pricing plans..." />;
    }

    // Error state
    if (error) {
        return (
            <div
                className="error-state"
                style={{
                    minHeight: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                }}
            >
                <p style={{ color: 'var(--danger)', fontSize: '18px' }}>
                    Failed to load pricing plans.
                </p>
                <p style={{ color: 'var(--muted)' }}>{error}</p>
            </div>
        );
    }

    return (
        <>
            {/* Hero Section */}
            <div className="page-hero" style={{ padding: '140px 32px 80px' }}>
                <div className="hero-premium-gradient-1"></div>
                <div className="hero-premium-gradient-2"></div>
                <div className="hero-premium-gradient-3"></div>

                <div style={{ position: 'relative', zIndex: 2 }}>
                    <div
                        className="eyebrow light"
                        style={{ display: 'inline-flex', marginBottom: '20px' }}
                    >
                        <Gem size={14} style={{ marginRight: '6px' }} />
                        Pricing
                    </div>
                    <h1
                        style={{
                            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                            marginBottom: '16px',
                        }}
                    >
                        Choose the plan that <span className="highlight">fits your church.</span>
                    </h1>
                    <p
                        style={{
                            fontSize: 'clamp(1rem, 1.2vw, 1.15rem)',
                            color: 'rgba(255,255,255,0.6)',
                            maxWidth: '560px',
                            margin: '0 auto',
                        }}
                    >
                        Affordable, transparent pricing designed for Ghanaian churches of every
                        size.
                    </p>
                </div>
            </div>

            {/* Billing Toggle */}
            <section className="section-light" style={{ padding: '32px 0 60px' }}>
                <div className="wrap">
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '6px',
                            flexWrap: 'wrap',
                            background: '#fff',
                            padding: '6px',
                            borderRadius: '12px',
                            boxShadow: 'var(--shadow-sm)',
                            maxWidth: '520px',
                            margin: '0 auto 32px',
                            border: '1px solid var(--border)',
                        }}
                    >
                        {billingOptions.map((option) => {
                            const isActive = billingCycle === option.id;
                            return (
                                <button
                                    key={option.id}
                                    onClick={() => setBillingCycle(option.id)}
                                    style={{
                                        flex: 1,
                                        padding: '10px 14px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: isActive
                                            ? 'var(--gradient-gold)'
                                            : 'transparent',
                                        color: isActive ? 'var(--navy)' : 'var(--ink-soft)',
                                        fontWeight: isActive ? 700 : 500,
                                        fontSize: 'clamp(11px, 1vw, 12px)',
                                        transition:
                                            'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        position: 'relative',
                                        boxShadow: isActive ? 'var(--shadow-gold)' : 'none',
                                        minWidth: '60px',
                                        whiteSpace: 'nowrap',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.background = 'var(--cream)';
                                            e.currentTarget.style.transform = 'scale(1.02)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }
                                    }}
                                >
                                    <span style={{ fontSize: 'clamp(12px, 1.2vw, 14px)' }}>
                                        {option.icon}
                                    </span>
                                    <span style={{ fontSize: 'clamp(10px, 0.9vw, 12px)' }}>
                                        {option.label}
                                    </span>
                                    {option.discount && (
                                        <span
                                            style={{
                                                fontSize: 'clamp(7px, 0.7vw, 8px)',
                                                fontWeight: 700,
                                                background: isActive
                                                    ? 'rgba(10,22,40,0.15)'
                                                    : 'rgba(46,204,113,0.12)',
                                                color: isActive ? 'var(--navy)' : '#27AE60',
                                                padding: '2px 6px',
                                                borderRadius: '10px',
                                                marginLeft: '2px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            -{option.discount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {billingCycle !== 'monthly' && (
                        <div
                            style={{
                                textAlign: 'center',
                                marginBottom: '24px',
                                fontSize: 'clamp(12px, 1vw, 13px)',
                                color: 'var(--green-dark)',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                flexWrap: 'wrap',
                            }}
                        >
                            <TrendingUp size={16} />
                            Save up to 20% with annual billing
                        </div>
                    )}

                    {/* Pricing Cards */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '24px',
                            alignItems: 'stretch',
                        }}
                    >
                        {plans.map((plan) => {
                            const Icon = plan.icon;
                            const displayPrice = plan.is_custom
                                ? 'Custom'
                                : getAdjustedPrice(plan, billingCycle);
                            const periodText = plan.is_custom ? '' : getPeriodText(billingCycle);
                            const isSelected = selectedPlan === plan.id;
                            const isHovered = hoveredPlan === plan.id;
                            const isFree = plan.price === 0 && !plan.is_custom;
                            const savings = getSavings(plan, billingCycle);

                            return (
                                <div
                                    key={plan.id}
                                    className={`pricing-card ${plan.is_featured ? 'featured' : ''} ${
                                        isSelected ? 'selected' : ''
                                    }`}
                                    onClick={() => setSelectedPlan(plan.id)}
                                    onMouseEnter={() => setHoveredPlan(plan.id)}
                                    onMouseLeave={() => setHoveredPlan(null)}
                                    style={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        background: '#fff',
                                        border: isSelected
                                            ? '3px solid var(--gold)'
                                            : plan.is_featured
                                            ? '2px solid var(--gold)'
                                            : '1px solid var(--border)',
                                        boxShadow: isHovered
                                            ? 'var(--shadow-lg)'
                                            : plan.is_featured
                                            ? 'var(--shadow-gold)'
                                            : 'var(--shadow-sm)',
                                        transform: isHovered
                                            ? 'translateY(-8px) scale(1.01)'
                                            : isSelected
                                            ? 'translateY(-4px)'
                                            : 'translateY(0)',
                                        transition:
                                            'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        borderRadius: 'var(--radius-lg)',
                                        padding: 'clamp(20px, 2.5vw, 32px) clamp(16px, 2vw, 28px)',
                                        minHeight: '380px',
                                    }}
                                >
                                    {plan.is_featured && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '-12px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                background: 'var(--gradient-gold)',
                                                color: 'var(--navy)',
                                                fontSize: 'clamp(10px, 0.8vw, 11px)',
                                                fontWeight: 700,
                                                padding: '4px 16px',
                                                borderRadius: 'var(--radius-full)',
                                                whiteSpace: 'nowrap',
                                                boxShadow: 'var(--shadow-gold)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                width: 'auto',
                                                textAlign: 'center',
                                                zIndex: 10,
                                            }}
                                        >
                                            <Star size={14} /> Most popular
                                        </div>
                                    )}

                                    {isSelected && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '12px',
                                                right: '12px',
                                                background: 'var(--gold)',
                                                color: 'var(--navy)',
                                                fontSize: 'clamp(9px, 0.7vw, 10px)',
                                                fontWeight: 700,
                                                padding: '4px 10px',
                                                borderRadius: 'var(--radius-full)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                zIndex: 5,
                                            }}
                                        >
                                            <Check size={14} /> Selected
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'clamp(10px, 1.2vw, 12px)',
                                            marginBottom: '12px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 'clamp(36px, 4vw, 44px)',
                                                height: 'clamp(36px, 4vw, 44px)',
                                                borderRadius: 'var(--radius-md)',
                                                background: 'rgba(0,0,0,0.04)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease',
                                                transform: isHovered
                                                    ? 'scale(1.1) rotate(-5deg)'
                                                    : 'scale(1)',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Icon size={22} style={{ color: plan.color || 'var(--gold)' }} />
                                        </div>
                                        <div>
                                            <div
                                                className="pricing-name"
                                                style={{
                                                    fontSize: 'clamp(12px, 1vw, 14px)',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px',
                                                    color: 'var(--muted)',
                                                }}
                                            >
                                                {plan.name}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            fontSize: plan.is_custom
                                                ? 'clamp(24px, 2.5vw, 28px)'
                                                : 'clamp(28px, 3vw, 38px)',
                                            fontFamily: 'Georgia, serif',
                                            color: 'var(--navy)',
                                            fontWeight: 700,
                                            margin: '8px 0 4px',
                                        }}
                                    >
                                        {plan.is_custom
                                            ? 'Custom'
                                            : isFree
                                            ? `GH₵ 0`
                                            : `GH₵ ${displayPrice}`}
                                        {!isFree && !plan.is_custom && (
                                            <span
                                                style={{
                                                    fontSize: 'clamp(12px, 1vw, 14px)',
                                                    color: 'var(--muted)',
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 500,
                                                    marginLeft: '4px',
                                                }}
                                            >
                                                {periodText}
                                            </span>
                                        )}
                                        {isFree && (
                                            <span
                                                style={{
                                                    fontSize: 'clamp(12px, 1vw, 14px)',
                                                    color: 'var(--muted)',
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 500,
                                                    marginLeft: '4px',
                                                }}
                                            >
                                                /mo
                                            </span>
                                        )}
                                    </div>

                                    {savings && (
                                        <div
                                            style={{
                                                fontSize: 'clamp(11px, 0.9vw, 12px)',
                                                color: 'var(--green-dark)',
                                                fontWeight: 600,
                                                marginTop: '2px',
                                                marginBottom: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                            }}
                                        >
                                            <Percent size={12} /> {savings}
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            fontSize: 'clamp(12px, 0.9vw, 13px)',
                                            color: 'var(--ink-soft)',
                                            marginBottom: '16px',
                                            lineHeight: 1.5,
                                            minHeight: 'clamp(40px, 4vw, 44px)',
                                        }}
                                    >
                                        {plan.description}
                                    </div>

                                    <ul
                                        style={{
                                            flex: 1,
                                            marginBottom: '16px',
                                            padding: 0,
                                            listStyle: 'none',
                                        }}
                                    >
                                        {plan.features.map((feature, i) => (
                                            <li
                                                key={i}
                                                style={{
                                                    fontSize: 'clamp(12px, 0.9vw, 13px)',
                                                    color: 'var(--ink-soft)',
                                                    padding: 'clamp(6px, 0.5vw, 8px) 0',
                                                    borderTop: '1px solid var(--border)',
                                                    display: 'flex',
                                                    gap: '8px',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Check
                                                    size={16}
                                                    className="check"
                                                    style={{ color: 'var(--green)', flexShrink: 0 }}
                                                />{' '}
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <Link
                                        to={plan.cta_link || '/signup'}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            display: 'block',
                                            textAlign: 'center',
                                            padding: 'clamp(12px, 1vw, 14px)',
                                            borderRadius: 'var(--radius-md)',
                                            fontWeight: 700,
                                            fontSize: 'clamp(13px, 1vw, 14px)',
                                            background:
                                                plan.is_featured || isSelected
                                                    ? 'var(--gradient-gold)'
                                                    : 'var(--cream)',
                                            color:
                                                plan.is_featured || isSelected
                                                    ? 'var(--navy)'
                                                    : 'var(--ink)',
                                            transition: 'all 0.3s ease',
                                            marginTop: 'auto',
                                            boxShadow:
                                                plan.is_featured || isSelected
                                                    ? 'var(--shadow-gold)'
                                                    : 'none',
                                            textDecoration: 'none',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (plan.is_featured || isSelected) {
                                                e.currentTarget.style.boxShadow =
                                                    'var(--shadow-gold-lg)';
                                                e.currentTarget.style.transform = 'scale(1.02)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (plan.is_featured || isSelected) {
                                                e.currentTarget.style.boxShadow =
                                                    'var(--shadow-gold)';
                                                e.currentTarget.style.transform = 'scale(1)';
                                            }
                                        }}
                                    >
                                        {plan.cta_text || 'Get Started'}{' '}
                                        <ArrowRight
                                            size={16}
                                            style={{ display: 'inline', verticalAlign: 'middle' }}
                                        />
                                    </Link>
                                </div>
                            );
                        })}
                    </div>

                    <div
                        style={{
                            textAlign: 'center',
                            fontSize: 'clamp(12px, 0.9vw, 13px)',
                            color: 'var(--muted)',
                            marginTop: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Shield size={16} style={{ display: 'inline', verticalAlign: 'middle' }} />
                        All plans include a 30-day free trial. No credit card required.
                        <Link
                            to="/faq"
                            style={{
                                color: 'var(--gold)',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            <HelpCircle size={14} /> See FAQ
                        </Link>
                    </div>
                </div>
            </section>

            {/* Custom Plans Section */}
            <section className="premium-cta-section" style={{ padding: 'clamp(60px, 8vw, 80px) 0' }}>
                <div className="wrap">
                    <div
                        style={{
                            maxWidth: '600px',
                            margin: '0 auto',
                            textAlign: 'center',
                            position: 'relative',
                            zIndex: 2,
                        }}
                    >
                        <div
                            style={{
                                display: 'inline-block',
                                padding: '6px 16px',
                                background: 'rgba(212, 175, 55, 0.12)',
                                border: '1px solid rgba(212, 175, 55, 0.2)',
                                borderRadius: 'var(--radius-full)',
                                fontSize: 'clamp(11px, 0.9vw, 12px)',
                                fontWeight: 600,
                                color: 'var(--gold-light)',
                                letterSpacing: '0.5px',
                                marginBottom: '16px',
                            }}
                        >
                            <Zap size={14} style={{ display: 'inline', marginRight: '6px' }} />
                            Need something different?
                        </div>

                        <h2
                            style={{
                                fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                                marginBottom: '12px',
                                lineHeight: 1.1,
                                color: '#fff',
                            }}
                        >
                            Custom plans <span className="highlight">available.</span>
                        </h2>

                        <p
                            style={{
                                fontSize: 'clamp(14px, 1.2vw, 16px)',
                                color: 'rgba(255,255,255,0.6)',
                                marginBottom: '28px',
                                lineHeight: 1.8,
                            }}
                        >
                            We work with churches to create custom plans that fit your specific needs
                            and budget. From additional SMS credits to custom integrations — we've
                            got you covered.
                        </p>

                        <div
                            style={{
                                display: 'flex',
                                gap: '16px',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Link
                                to="/contact"
                                className="btn-premium-primary large"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: 'clamp(14px, 1.5vw, 20px) clamp(24px, 3vw, 40px)',
                                    background: 'var(--gradient-gold)',
                                    color: 'var(--navy)',
                                    fontWeight: 700,
                                    fontSize: 'clamp(14px, 1vw, 17px)',
                                    borderRadius: 'var(--radius-md)',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    boxShadow: '0 4px 20px rgba(212, 175, 55, 0.3)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: 'clamp(48px, 5vw, 64px)',
                                    textDecoration: 'none',
                                }}
                            >
                                Contact our team{' '}
                                <ArrowRight size={18} className="btn-premium-icon" />
                            </Link>
                            <Link
                                to="/demo"
                                className="btn-premium-secondary large"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: 'clamp(14px, 1.5vw, 20px) clamp(24px, 3vw, 40px)',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: 'clamp(14px, 1vw, 17px)',
                                    borderRadius: 'var(--radius-md)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    backdropFilter: 'blur(10px)',
                                    minHeight: 'clamp(48px, 5vw, 64px)',
                                    textDecoration: 'none',
                                }}
                            >
                                Schedule a demo
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Teaser (static) */}
            <section style={{ background: '#fff', padding: 'clamp(48px, 6vw, 60px) 0' }}>
                <div className="wrap">
                    <div className="section-head center">
                        <div className="eyebrow" style={{ margin: '0 auto' }}>
                            <HelpCircle size={14} style={{ marginRight: '6px' }} />
                            FAQ
                        </div>
                        <h2
                            style={{
                                fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
                                color: 'var(--navy)',
                                marginTop: '16px',
                                lineHeight: 1.12,
                                letterSpacing: '-0.03em',
                            }}
                        >
                            Common <span className="highlight">questions</span>
                        </h2>
                    </div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '24px',
                            maxWidth: '800px',
                            margin: '0 auto',
                        }}
                    >
                        <div
                            style={{
                                padding: 'clamp(16px, 2vw, 20px) clamp(16px, 2vw, 24px)',
                                background: 'var(--cream)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border)',
                            }}
                        >
                            <h4
                                style={{
                                    fontSize: 'clamp(14px, 1vw, 15px)',
                                    color: 'var(--navy)',
                                    marginBottom: '4px',
                                }}
                            >
                                Can I switch plans anytime?
                            </h4>
                            <p
                                style={{
                                    fontSize: 'clamp(13px, 0.9vw, 14px)',
                                    color: 'var(--ink-soft)',
                                    lineHeight: 1.6,
                                }}
                            >
                                Yes! You can upgrade or downgrade your plan at any time.
                            </p>
                        </div>

                        <div
                            style={{
                                padding: 'clamp(16px, 2vw, 20px) clamp(16px, 2vw, 24px)',
                                background: 'var(--cream)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border)',
                            }}
                        >
                            <h4
                                style={{
                                    fontSize: 'clamp(14px, 1vw, 15px)',
                                    color: 'var(--navy)',
                                    marginBottom: '4px',
                                }}
                            >
                                Is there a contract?
                            </h4>
                            <p
                                style={{
                                    fontSize: 'clamp(13px, 0.9vw, 14px)',
                                    color: 'var(--ink-soft)',
                                    lineHeight: 1.6,
                                }}
                            >
                                No long-term contracts. Month-to-month billing with the freedom to
                                cancel anytime.
                            </p>
                        </div>

                        <div
                            style={{
                                padding: 'clamp(16px, 2vw, 20px) clamp(16px, 2vw, 24px)',
                                background: 'var(--cream)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border)',
                            }}
                        >
                            <h4
                                style={{
                                    fontSize: 'clamp(14px, 1vw, 15px)',
                                    color: 'var(--navy)',
                                    marginBottom: '4px',
                                }}
                            >
                                Do you offer discounts for non-profits?
                            </h4>
                            <p
                                style={{
                                    fontSize: 'clamp(13px, 0.9vw, 14px)',
                                    color: 'var(--ink-soft)',
                                    lineHeight: 1.6,
                                }}
                            >
                                Yes! Churches and non-profits receive a 10% discount on all plans.
                            </p>
                        </div>

                        <div
                            style={{
                                padding: 'clamp(16px, 2vw, 20px) clamp(16px, 2vw, 24px)',
                                background: 'var(--cream)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border)',
                            }}
                        >
                            <h4
                                style={{
                                    fontSize: 'clamp(14px, 1vw, 15px)',
                                    color: 'var(--navy)',
                                    marginBottom: '4px',
                                }}
                            >
                                What payment methods do you accept?
                            </h4>
                            <p
                                style={{
                                    fontSize: 'clamp(13px, 0.9vw, 14px)',
                                    color: 'var(--ink-soft)',
                                    lineHeight: 1.6,
                                }}
                            >
                                We accept credit/debit cards, bank transfers, and mobile money.
                            </p>
                        </div>
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                        <Link
                            to="/faq"
                            className="btn-ghost"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: 'clamp(12px, 1vw, 16px) clamp(24px, 2vw, 32px)',
                                borderRadius: 'var(--radius-md)',
                                border: '1.5px solid var(--border)',
                                color: 'var(--navy)',
                                fontWeight: 600,
                                fontSize: 'clamp(13px, 1vw, 15px)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                background: 'transparent',
                            }}
                        >
                            View all FAQs <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}

export default Pricing;